<template>
    <div class="tab-bar-controller">
        <header v-if="showTopBar">
            <div class="left">
                <InheritComponent name="tabbar-left" />
            </div>
            <div class="middle">
                <template v-if="tabs.length > 1">
                    <button v-for="(item, index) in tabs" :key="index" class="button item" :class="{ selected: item.isSelected(selectedItem) }" type="button" data-testid="tab-button" :data-tab-id="item.isGroup ? 'more' : ('id' in item ? item.id : 'more')" @click="(event) => selectTab(event, item)">
                        <div class="button text" :class="{ selected: item.isSelected(selectedItem) }">
                            <span :class="'icon '+item.icon" />
                            <span>{{ item.name }}</span>
                            <span v-if="unref(item.badge)" class="bubble">{{ unref(item.badge) }}</span>
                            <span v-if="item.isGroup" class="icon arrow-down-small gray" />
                        </div>
                    </button>
                </template>
            </div>
            <div class="right">
                <InheritComponent name="tabbar-notifications" />
                <InheritComponent name="tabbar-right" />
            </div>
        </header>
        <div ref="mainElement" :class="{main: true, showTopBar, showBottomBar, shouldHideBottomBar: showBottomBar && shouldHideBottomBar}">
            <FramedComponent v-if="root" :key="root.key" :root="root" />
        </div>

        <footer v-if="showBottomBar" :class="{hidden: shouldHideBottomBar}">
            <button v-for="(item, index) in tabs" :key="index" class="button item" :class="{ selected: item.isSelected(selectedItem) }" type="button" data-testid="tab-button" :data-tab-id="item.isGroup ? 'more' : ('id' in item ? item.id : 'more')" @click="(event) => selectTab(event, item)">
                <div class="button text small column" :class="{ selected: item.isSelected(selectedItem) }">
                    <span :class="'icon '+item.icon" />
                    <span>
                        {{ item.name }}
                    </span>
                    <span v-if="unref(item.badge)" class="bubble">{{ unref(item.badge) }}</span>
                </div>
            </button>
        </footer>
    </div>
</template>

<script lang="ts">
import type { PushOptions } from '@browser/navigation';
import { inject, shallowRef } from 'vue';

import { GlobalEventBus } from '@stamhoofd/frontend-application/EventBus';
import { useDeviceWidth } from '#hooks/useDeviceWidth.ts';
import { getUrls } from './TranslatedUrl.ts';

type TabBarControllerInstance = {
    returnToHistoryIndex(): boolean | undefined;
    show(options: PushOptions): Promise<void>;
    shouldNavigateAway(): Promise<boolean>;
    selectTabById(id: string): Promise<void>;
};

export function useTabBarController(): Ref<TabBarControllerInstance> {
    const c = inject('reactive_tabBarController') as TabBarControllerInstance | Ref<TabBarControllerInstance>;
    return shallowRef(c);
}

export function useHideTabBar() {
    const reference = {}; // new object reference
    const injector = inject<any>('reactive_hide_tab_bar', null);

    const register = () => {
        const unwrapped = unref(injector);
        if (unwrapped && unwrapped.register) {
            unwrapped.register(reference);
        }
    };

    const unregister = () => {
        const unwrapped = unref(injector);
        if (unwrapped && unwrapped.unregister) {
            unwrapped.unregister(reference);
        }
    };

    onMounted(register);
    onActivated(register);
    onDeactivated(unregister);
    onBeforeUnmount(unregister);
}

</script>

<script setup lang="ts">
import { AsyncComponent } from '#containers/AsyncComponent.ts';
import { ComponentWithProperties, defineRoutes, FramedComponent, HistoryManager, NavigationController, ReactiveUrl, usePresent, useUrl } from '@browser/navigation';
import { Formatter } from '@stamhoofd/utility';
import type { ComponentPublicInstance, Ref } from 'vue';
import { computed, getCurrentInstance, nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, provide, ref, toRaw, unref } from 'vue';

import InheritComponent from './InheritComponent.vue';
import { TabBarItem, TabBarItemGroup } from './TabBarItem';

const props = defineProps<{
    /** Reusable tab items; switching preserves mounted state and internal scroll positions. Reselecting can scroll to top or reset the tab. */
    tabs: ((TabBarItem | TabBarItemGroup)[]) | Ref<(TabBarItem | TabBarItemGroup)[]>;
}>();

defineOptions({
    name: $t(`%2M`),
});

type TabBarItemWithComponent = TabBarItem & Required<Pick<TabBarItem, 'component'>>;
const tabs = computed(() => {
    const base = unref(props.tabs);

    if (!showTopBar.value) {
        // Replace groups with tabs
        return base.flatMap((t) => {
            if (t instanceof TabBarItemGroup) {
                return [
                    new TabBarItem({
                        ...t,
                        component: new ComponentWithProperties(NavigationController, {
                            root: AsyncComponent(() => import('./TabBarDropdownView.vue'), {
                                group: t,
                                selectedItem: selectedItem,
                                selectItem: selectItem,
                            }),
                        }),
                    }),
                ];
            }
            return [t];
        });
    }

    return base;
});

const flatTabs = computed<TabBarItemWithComponent[]>(() => unref(props.tabs).flatMap(t => t.items as TabBarItemWithComponent[]).filter(t => !!t.component));
const injectedComponents = inject('reactive_components') as Ref<Record<string, ComponentWithProperties> | undefined> | undefined;
const deviceWidth = useDeviceWidth();
const showTopBar = computed(() => deviceWidth.value > 1100);
const showBottomBar = computed(() => !showTopBar.value && tabs.value.length > 1);

const hieBottomBarRequesters = ref(new Set());
const shouldHideBottomBar = computed(() => {
    return hieBottomBarRequesters.value.size > 0;
});

provide('reactive_hide_tab_bar', {
    register: (reference: any) => {
        console.log('register');
        hieBottomBarRequesters.value.add(reference);
    },
    unregister: (reference: any) => {
        console.log('unregister');
        hieBottomBarRequesters.value.delete(reference);
    },
});

provide('reactive_components', computed(() => {
    if (!showTopBar.value) {
        return unref(injectedComponents) ?? {};
    }
    // All displayed
    return {
        ...(unref(injectedComponents) ?? {}),
        'tabbar-replacement': null,
    };
}));

const selectedItem: Ref<TabBarItem | null> = ref(null) as any as Ref<TabBarItem | null>; // TypeScript is unpacking the TabBarItem to {...} for some reason

// Root is stored separately because we can also navigate to non-tabs
const root: Ref<ComponentWithProperties | null> = ref(null) as any as Ref<ComponentWithProperties | null>;

const mainElement = ref<HTMLElement | null>(null);
const urlHelpers = useUrl();
const present = usePresent();

console.log('Created TabBarController');

defineRoutes(flatTabs.value.map((tab) => {
    const name = unref(tab.name);
    return {
        name,
        ...(tab.url ? getUrls(tab.url) : { url: tab.id, alternativeUrls: [Formatter.slug(name)] }),
        isDefault: {},
        handler: async (options) => {
            console.log('Select item handler TabBarController', options);
            if (options.checkRoutes) {
                tab.component.setCheckRoutes();
            }
            await selectItem(tab, options.adjustHistory, { url: options.url });
        },
    };
}));

const instance = getCurrentInstance();
provide('reactive_tabBarController', instance?.proxy); // Sadly the proxy does not include exposed properties - ComponentWithProperties has a workaround at getExposeProxy

const getInternalScrollElements = () => {
    return (mainElement.value?.querySelectorAll('.st-view > main') ?? []) as NodeListOf<HTMLElement>;
};

const saveCurrentItemState = () => {
    const old = selectedItem.value;
    if (old && old.component) {
        // Keep current item alive
        old.component.keepAlive = true;

        // Save scroll position
        const scrollElements = getInternalScrollElements();

        // Clear already saved items
        old.savedScrollPositions = new WeakMap();

        for (const element of scrollElements) {
            old.savedScrollPositions.set(element, element.scrollTop);
        }
    }
};

const shouldNavigateAway = async () => {
    const old = root.value;
    if (old) {
        return await old.shouldNavigateAway();
    }
    return true;
};

async function selectItem(item: TabBarItem, appendHistory: boolean = true, options?: { force?: boolean; url?: ReactiveUrl | Ref<ReactiveUrl | string | null> | string | null }) {
    let save = true;
    if (toRaw(item) === toRaw(selectedItem.value)) {
        save = false;
        if (!options?.force) {
        // Try to scroll this item to the top
            if (mainElement.value) {
                let didScroll = false;
                const scrollElements = mainElement.value.querySelectorAll('.st-view > main');
                if (scrollElements) {
                    for (const scrollElement of scrollElements) {
                        if (scrollElement.scrollTop !== 0) {
                        // Scroll to top animated
                            scrollElement.scrollTo({
                                top: 0,
                                behavior: 'smooth',
                            });
                            didScroll = true;
                        }
                    }
                }

                if (!didScroll) {
                    if (selectedItem.value?.component) {
                        const component = selectedItem.value?.component;

                        // Reset state
                        const clone = component.deepClone();
                        selectedItem.value.component = clone;
                        await selectItem(item, appendHistory, { force: true });
                    }
                }
            }
            return;
        }
    }

    if (!await shouldNavigateAway()) {
        return;
    }

    if (!item.component) {
        if (item.action) {
            await item.action.call(instance!.proxy as ComponentPublicInstance);
        }
        return;
    }
    if (save) {
        saveCurrentItemState();
    }
    const old = selectedItem.value;

    // Set url namespace of the tab
    const defaultTabUrl = (item.url ? getUrls(item.url) : { url: item.id, alternativeUrls: [Formatter.slug(item.name)] }).url;
    item.component.provide.reactive_navigation_url = computed(() => {
        // prefer the url that was passed, because this contains the 'matched' url (E.g. 'members' was matched, while the normal url is 'leden', which is required for further url matching)
        const u = (options?.url ? unref(options?.url) : null) ?? defaultTabUrl;
        return urlHelpers.extendUrl((typeof u === 'string') ? new ReactiveUrl({ url: u }) : u);
    });

    if (item.component.hasHistoryIndex()) {
        item.component.returnToHistoryIndex();
    } else {
        item.component.deleteHistoryIndex();
        HistoryManager.pushState(null, old
            ? async () => {
                await selectItem(old, false);
            }
            : null, { adjustHistory: appendHistory });
        item.component.assignHistoryIndex();
    }

    // Switch
    selectedItem.value = item;
    root.value = item.component;

    const positions = item.savedScrollPositions;
    await nextTick();

    const newScrollElements = getInternalScrollElements();

    // Restore scroll position
    // Let the OS rerender once so all the positions are okay after dom insertion

    for (const element of newScrollElements) {
        const position = positions.get(element);
        if (!position) {
            continue;
        }
        element.scrollTop = position;
    }
}

const selectTab = async (event: MouseEvent, tab: TabBarItem | TabBarItemGroup) => {
    if (tab instanceof TabBarItem) {
        return selectItem(tab);
    }

    if (!showTopBar.value) {
        // Treat as a normal tab
        return;
    }

    const padding = 15;
    let width = 400;
    const button = event.currentTarget as HTMLElement;
    const bounds = button.getBoundingClientRect();
    const win = window,
        doc = document,
        docElem = doc.documentElement,
        body = doc.getElementsByTagName('body')[0],
        clientWidth = win.innerWidth || docElem.clientWidth || body.clientWidth;

    let left = bounds.left - padding;

    if (left + width > clientWidth + padding) {
        left = clientWidth - padding - width;

        if (left < padding) {
            left = padding;
            width = clientWidth - padding * 2;
        }
    }

    // Open dropdown menu
    await present({
        components: [
            new ComponentWithProperties(NavigationController, {
                root: AsyncComponent(() => import('./TabBarDropdownView.vue'), {
                    group: tab,
                    selectedItem: selectedItem,
                    selectItem: selectItem,
                }),
            }, {
                provide: {
                    reactive_navigation_disable_url: true,
                },
            }),
        ],
        modalDisplayStyle: 'popup',
        modalClass: 'positionable-sheet',
        modalCssStyle: '--sheet-position-left: ' + left.toFixed(1) + 'px; --sheet-position-top: 65px; --sheet-vertical-padding: 15px; --st-popup-width: ' + width.toFixed(1) + 'px; ',
    });
};

const selectTabById = async (id: string) => {
    const item = flatTabs.value.find(tab => tab.id === id);
    if (item) {
        await selectItem(item);
    } else {
        console.error('No tab item found with id:', id);
    }
};

const show = async (options: PushOptions) => {
    console.log('Showing manually', options);
    if (options.components.length > 1) {
        throw new Error('Impossible to show more than 1 component from a direct child of the TabBarController');
    }
    if (!await shouldNavigateAway()) {
        return;
    }
    const component = options.components[0];

    const foundItem = flatTabs.value.find(tab => tab.component === component);
    if (foundItem) {
        return selectItem(foundItem);
    }

    if (!component || component === root.value) {
        return;
    }

    saveCurrentItemState();

    const old = selectedItem.value;
    HistoryManager.pushState(null, old
        ? async () => {
            await selectItem(old, false);
        }
        : null, {
        adjustHistory: options?.adjustHistory ?? true,
        invalid: options?.invalidHistory ?? false,
    });
    component.assignHistoryIndex();

    // Switch
    selectedItem.value = null;
    root.value = component;

    // Wait for mount
    await nextTick();
};
provide('reactive_navigation_show', show);

onMounted(() => {
    console.log('Mounted TabBarController');
    GlobalEventBus.addListener(this, 'selectTabById', selectTabById);
});

onBeforeUnmount(() => {
    GlobalEventBus.removeListener(this);

    // Prevent memory issues by removing all references and destroying kept alive components
    for (const { component } of flatTabs.value) {
        // Destroy them one by one
        if (component.isKeptAlive && component.vnode) {
            component.destroy(component.vnode);
        }
    }
});

const returnToHistoryIndex = () => {
    return root.value?.returnToHistoryIndex();
};

defineExpose({
    returnToHistoryIndex,
    show,
    shouldNavigateAway,
    selectTabById,
} satisfies TabBarControllerInstance);

</script>

<style lang="scss">
@use '@stamhoofd/scss/base/variables' as *;

.tab-bar-controller {
    --tab-bar-header-height: 80px;
    height: calc(var(--vh, 1vh) * 100);
    max-height: 100vh;
    max-height: 100dvh;
    overflow: hidden;
    overflow: clip; // More modern + disables scrolling
    --saved-vh: var(--vh, 1vh);
    --saved-keyboard-height: var(--keyboard-height, 0px);
    --saved-st-safe-area-bottom: var(--st-safe-area-bottom, 0px);
    --saved-st-safe-area-top: var(--st-safe-area-top, 0px);

    > header {
        box-sizing: border-box;
        height: calc(var(--tab-bar-header-height) + var(--st-safe-area-top, 0px));
        border-bottom: $border-width-thin solid $color-border;
        background: $color-background;
        padding-top: var(--st-safe-area-top, 0px);

        display: grid;
        grid-template-columns: 1fr auto 1fr;
        align-items: center;

        > .left {
            padding-left: 20px;
        }

        > .right {
            padding-right: 20px;

            // Align grid items right
            justify-self: end;
            display: flex;
            align-items: center;
            gap: 20px;
        }

        > .middle {
            display: flex;
            flex-direction: row;
            gap: 45px;
            justify-content: center;

            > .item {
                display: block;
                height: var(--tab-bar-header-height);

                > div {
                    height: var(--tab-bar-header-height);

                    display: flex;
                    text-align: center;
                    align-items: center;
                    justify-content: center;
                }
            }
        }
    }

    > .main {
        // Pass an update to the --vh because we removed some
        background: var(--color-current-background);

        &.showTopBar {
            --vh: calc(var(--saved-vh, 1vh) - (var(--tab-bar-header-height) + var(--saved-st-safe-area-top, 0px)) / 100);
            --st-safe-area-top: 0px; // Handled by header
        }

        &.showBottomBar {
            --st-safe-area-bottom: calc(var(--saved-st-safe-area-bottom) + var(--tab-bar-header-height)); // Handled by footer

            &.shouldHideBottomBar {
                --st-safe-area-bottom: var(--saved-st-safe-area-bottom);
            }
        }

        height: calc(var(--vh, 1vh) * 100);

        // No scrolling here allowed. Child components should manage this
        overflow: hidden;
        overflow: clip; // More modern + disables scrolling
    }

    > footer {
        height: var(--tab-bar-header-height);
        //border-top: $border-width-thin solid $color-border;
        background: $color-background;
        background: color-mix(in srgb, $color-background 70%, transparent);
        padding-bottom: var(--st-safe-area-bottom, 0px);
        user-select: none;

        backdrop-filter: blur(10px);

        transition: transform 0.2s, opacity 0.2s;

        body.web-android &, body.native-android & {
            background: $color-background-shade;
            backdrop-filter: none;
            border-top: 0;
        }

        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1000;

        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(0, 1fr));

        > button {
            min-width: 0;
            overflow: hidden;
        }

        &.hidden {
            opacity: 0;
            transform: translateY(100%);
        }
    }
}

</style>
